import React, { useState, useMemo, useCallback, useRef } from 'react';
import { Dropdown } from '../dropdown';
import {
  Circles,
  ColorDots,
  ColorsDiv,
  ContentDiv,
  DropDiv,
  EmptyText,
  Icon,
  IconsContainer,
  IconsDiv,
  ImageContainer,
  SearchDiv,
  Span,
  SVGIconDiv,
  TabButtonDiv,
  TabItem,
  UploadButton,
  UploadImageDiv,
  UploadTextDiv
} from './styles';
import SVGIcon from '../../assets/images/svg/SVGIcon';
import { rootStore } from '../../mobx/rootStore';
import { observer } from 'mobx-react-lite';
import { ICON_COLORS, ICON_SIZE, PROJECT_ICON_TYPE } from '../../global/constants';
import IconSvg from '../../assets/images/svg/iconSvg';
import { projectIcon } from '../../global/row';
import ProjectIconAvatar from '../projectIconAvatar';
import { Icon as IconInterface } from '../../mobx/interfaces/project';
import { dispatchProjectSuccess, updateProjectData } from '../../services/projectServices';
import { ProjectModel } from '../../mobx/models/project';
import { captureException } from '../../services/logService';
import { getFirebaseUrlFromFile, isEmpty } from '../../helpers/common';
import EmojiPicker, { Theme } from 'emoji-picker-react';
import { useMobileDevice } from '../../global/useMobile';
import { CreateProjectInput } from '../../mobx/stores/projectStore';
import Input from '../input';
import Image from '../image';

interface Props {
  currentProject?: ProjectModel;
  iconSize?: number;
  size?: number;
  isCreateProject?: boolean;
  isTemplate?: boolean;
  isViewPage?: boolean;
  fontSize?: string;
}
const AddIconDropdown = (props: Props) => {
  const isMobile = useMobileDevice();
  const {
    currentProject,
    iconSize = 16,
    size = isMobile ? 30 : 26,
    isCreateProject,
    isTemplate = false,
    isViewPage,
    fontSize = '16px'
  } = props;
  //Ref
  const inputRef = useRef<HTMLInputElement | null>(null);
  const iconDropdownRef = useRef<{ closeDropdown: () => void }>(null);

  //State variables
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<'icons' | 'emojis' | 'image'>('icons');
  //Mobx store
  const { viewStore, projectStore, projectTemplateStore } = rootStore;
  const { createViewInput } = viewStore;
  const { createProjectInput } = projectStore;
  const { currentProjectTemplate } = projectTemplateStore;
  //Other variables
  const project = isTemplate
    ? currentProjectTemplate
    : isCreateProject
    ? createProjectInput
    : isViewPage
    ? createViewInput
    : rootStore.projectStore.currentProject;

  // Get all custom icons
  const allIcons = useMemo(() => {
    return projectIcon.map((icon) => ({
      name: icon.label,
      displayName: icon.label
        .split('-')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' '),
      component: (props: { size?: number }) => (
        <IconSvg name={icon.label} width='16px' height='16px' viewBox='0 0 20 20' fill='var(--text-secondary)' />
      )
    }));
  }, []);

  // Filter icons based on search query
  const filteredIcons = useMemo(() => {
    if (!searchQuery) return allIcons;
    return allIcons.filter((icon) => icon.displayName.toLowerCase().includes(searchQuery.toLowerCase()));
  }, [allIcons, searchQuery]);

  const handleSelectProjectIcon = useCallback(
    async (type: number, reference: string) => {
      try {
        iconDropdownRef.current?.closeDropdown();
        if (reference === projectIcon[0].label) {
          type = PROJECT_ICON_TYPE.DEFAULT;
          reference = '';
        }
        let updatedIcon: IconInterface;
        if (isTemplate) {
          updatedIcon = {
            ...currentProjectTemplate?.icon,
            type,
            reference
          };
          currentProjectTemplate?.setIcon(updatedIcon);
        } else if (isCreateProject) {
          updatedIcon = {
            ...createProjectInput?.icon,
            type,
            reference
          };
          createProjectInput?.setIcon(updatedIcon);
        } else if (isViewPage) {
          createViewInput.setIcon(type, reference);
        } else {
          updatedIcon = {
            ...currentProject?.icon,
            type,
            reference
          };
          const payload = {
            Icon: {
              Type: type,
              Reference: reference,
              Color: currentProject?.icon?.color
            }
          };
          rootStore.projectStore.currentProject?.setIcon(updatedIcon);
          const response = await updateProjectData(currentProject?.id, payload);
          if (response) {
            dispatchProjectSuccess('Project icon updated successfully');
            return true;
          }
        }
      } catch (error) {
        console.log('error', error);
      }
    },
    [
      createProjectInput,
      createViewInput,
      currentProject?.icon,
      currentProject?.id,
      currentProjectTemplate,
      isCreateProject,
      isTemplate,
      isViewPage
    ]
  );

  const handleSearch = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  }, []);

  const onSelectColor = useCallback(
    async (color: number) => {
      try {
        let updatedProjectIcon: IconInterface;
        if (isTemplate) {
          updatedProjectIcon = {
            ...currentProjectTemplate?.icon,
            color
          };
          currentProjectTemplate?.setIcon(updatedProjectIcon);
        } else if (isCreateProject) {
          updatedProjectIcon = {
            ...createProjectInput?.icon,
            color
          };
          createProjectInput?.setIcon(updatedProjectIcon);
        } else if (isViewPage) {
          createViewInput.setColor(color);
        } else {
          updatedProjectIcon = {
            ...currentProject?.icon,
            color
          };
          rootStore.projectStore.currentProject?.setIcon(updatedProjectIcon);
          const payload = {
            Icon: {
              Type: currentProject?.icon?.type || PROJECT_ICON_TYPE.DEFAULT,
              Reference: currentProject?.icon?.reference,
              Color: color
            }
          };
          const response = await updateProjectData(currentProject?.id, payload);
          if (response) {
            dispatchProjectSuccess('Project icon updated successfully');
            return true;
          }
        }
      } catch (error) {
        console.log('error', error);
      }
    },
    [
      createProjectInput,
      createViewInput,
      currentProject?.icon,
      currentProject?.id,
      currentProjectTemplate,
      isCreateProject,
      isTemplate,
      isViewPage
    ]
  );

  // handel upload click
  const handleUploadClick = useCallback(() => {
    inputRef.current?.click();
  }, []);

  // for convert base64
  const convertBase64 = (file: File) => {
    return new Promise((resolve, reject) => {
      const fileReader = new FileReader();
      fileReader.readAsDataURL(file);
      fileReader.onload = async () => {
        try {
          const item = {
            profile: fileReader.result,
            file: file,
            name: currentProject?.name || createViewInput?.name || 'view-icon'
          };
          let profileUrl;
          if (item) {
            profileUrl = await getFirebaseUrlFromFile(item, `project-icon/`, currentProject?.id);
          }
          const updatedIcon = {
            type: PROJECT_ICON_TYPE.IMAGE,
            reference: profileUrl
          };
          if (isTemplate) {
            currentProjectTemplate?.setIcon(updatedIcon);
          } else if (isCreateProject) {
            rootStore.projectStore.createProjectInput?.setIcon(updatedIcon);
          } else if (isViewPage) {
            if (profileUrl) {
              createViewInput.setIcon(PROJECT_ICON_TYPE.IMAGE, profileUrl);
            }
          } else {
            rootStore.projectStore.currentProject?.setIcon(updatedIcon);
            const payload = {
              Icon: {
                Type: PROJECT_ICON_TYPE.IMAGE,
                Reference: profileUrl
              }
            };
            const response = await updateProjectData(currentProject?.id, payload);
            if (response) {
              rootStore.alertNotificationStore.setSuccessMessage('Project icon updated successfully!');
            }
          }

          resolve(profileUrl);
        } catch (err) {
          reject(err);
        }
      };
      fileReader.onerror = (error) => reject(error);
    });
  };

  // handle file read
  const handleFileRead = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const file = event.target.files?.[0];
      if (!file) return;
      let fileData = file?.name.split('.');
      if (file.size > 500000) {
        rootStore.alertNotificationStore.setErrorMessage('Please upload less than 500kb photo size.');
        return false;
      }
      iconDropdownRef.current?.closeDropdown();
      const fileType = fileData[fileData.length - 1];
      if (fileType === 'jpg' || fileType === 'jpeg' || fileType === 'png') {
        await convertBase64(file);
      } else {
        rootStore.alertNotificationStore.setErrorMessage('Please upload a valid type photo.');
        return false;
      }
    } catch (e) {
      captureException(e);
    }
  };

  return (
    <>
      <input type='file' onChange={handleFileRead} ref={inputRef} hidden aria-label='Upload file' />

      <Dropdown
        isMinWidth={303}
        isRemoveMaxHeight={true}
        ref={iconDropdownRef}
        content={
          <>
            {isCreateProject && !createProjectInput?.icon && !createProjectInput?.name ? (
              <SVGIconDiv>
                <SVGIcon
                  name='pie-chart-icon'
                  width={ICON_SIZE.XL}
                  height={ICON_SIZE.XL}
                  viewBox={`0 0 ${ICON_SIZE.SM} ${ICON_SIZE.SM}`}
                  fill='var(--text-muted)'
                />
              </SVGIconDiv>
            ) : isViewPage &&
              (!project?.icon?.type || project?.icon?.type === PROJECT_ICON_TYPE.DEFAULT) &&
              isEmpty(project?.name) ? (
              <>
                <SVGIconDiv className='views'>
                  <SVGIcon
                    name='navbar-views-icon'
                    width={ICON_SIZE.MD}
                    height={ICON_SIZE.MD}
                    viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                    fill='var(--text-placeholder)'
                  />
                </SVGIconDiv>
              </>
            ) : (
              <ProjectIconAvatar
                size={size}
                type={project?.icon?.type ?? PROJECT_ICON_TYPE.DEFAULT}
                reference={project?.icon?.reference || ''}
                backgroundColor={project?.icon?.color ? ICON_COLORS[project?.icon?.color] : ''}
                projectName={project?.name || (project as CreateProjectInput)?.selectedTemplate?.name || ''}
                iconSize={iconSize}
                isClickable={true}
                fontSize={fontSize}
              />
            )}
          </>
        }>
        <DropDiv role='button' onClick={(e) => e.stopPropagation()}>
          <TabButtonDiv>
            <TabItem
              role='button'
              isActive={activeTab === 'icons'}
              onClick={(e) => {
                e.stopPropagation();
                setActiveTab('icons');
              }}>
              Icons
            </TabItem>
            <TabItem
              role='button'
              isActive={activeTab === 'emojis'}
              onClick={(e) => {
                e.stopPropagation();
                setActiveTab('emojis');
              }}>
              Emojis
            </TabItem>
            <TabItem
              role='button'
              isActive={activeTab === 'image'}
              onClick={(e) => {
                e.stopPropagation();
                setActiveTab('image');
              }}>
              Image
            </TabItem>
          </TabButtonDiv>
          <ContentDiv>
            {activeTab === 'icons' && (
              <ColorsDiv>
                <Circles>
                  <ColorDots>
                    {Object.entries(ICON_COLORS).map(([key, color]) => (
                      <Span role='button' key={key} color={color} onClick={() => onSelectColor(Number(key))}>
                        {/* show icon of selected color */}
                        {project?.icon?.color === Number(key) && (
                          <SVGIcon
                            name='correct-sign-icon'
                            width={ICON_SIZE.SM}
                            height={ICON_SIZE.SM}
                            viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                            fill='var(--text-white)'
                          />
                        )}
                      </Span>
                    ))}
                  </ColorDots>
                </Circles>
              </ColorsDiv>
            )}
            {activeTab === 'icons' ? (
              <IconsDiv>
                <SearchDiv role='button' onClick={(e) => e.stopPropagation()}>
                  <Input
                    inputSize='small'
                    placeHolder='Search icons'
                    value={searchQuery}
                    onChange={handleSearch}
                    className='input'
                  />
                </SearchDiv>
                <IconsContainer>
                  {filteredIcons.map((icon) => {
                    const IconComponent = icon.component;
                    return (
                      <Icon
                        role='button'
                        key={icon.name}
                        onClick={() => handleSelectProjectIcon(PROJECT_ICON_TYPE.ICON, icon.name)}
                        isActive={createViewInput.icon?.reference === icon.name}>
                        <IconComponent size={16} />
                      </Icon>
                    );
                  })}
                </IconsContainer>
                {searchQuery && filteredIcons.length === 0 && <EmptyText>No results found</EmptyText>}
              </IconsDiv>
            ) : activeTab === 'emojis' ? (
              <IconsDiv>
                <EmojiPicker
                  searchPlaceholder='Search Emoji'
                  theme={Theme.AUTO}
                  autoFocusSearch={true}
                  width={258}
                  height={240}
                  onEmojiClick={(data) => {
                    handleSelectProjectIcon(PROJECT_ICON_TYPE.EMOJI, data?.emoji);
                  }}
                  lazyLoadEmojis={true}
                />
              </IconsDiv>
            ) : (
              <IconsDiv>
                {/* Placeholder for image upload/select UI */}
                <UploadImageDiv>
                  <ImageContainer>
                    <Image
                      imageUrl={
                        project?.icon?.type === PROJECT_ICON_TYPE.IMAGE && project?.icon?.reference
                          ? project.icon.reference
                          : 'image_empty'
                      }
                      alt='profilePhoto'
                      width={48}
                      height={48}
                    />
                  </ImageContainer>
                  <UploadTextDiv>
                    <UploadButton role='button' onClick={handleUploadClick}>
                      Upload image
                    </UploadButton>
                    <p>Maximum file size allowed in 500 KB in jpg, jpeg and png format.</p>
                  </UploadTextDiv>
                </UploadImageDiv>
              </IconsDiv>
            )}
          </ContentDiv>
        </DropDiv>
      </Dropdown>
    </>
  );
};

export default observer(AddIconDropdown);
